/**
 * Quiet Days savings — how much less the user spent during a block session
 * than they normally would have.
 *
 * Baseline: the same calendar days shifted back 1, 2, and 3 weeks (so weekday
 * spending rhythms compare like-for-like) → take the MEDIAN of the three sums.
 * savings = baseline − actual (raw value may be negative; accumulation floors
 * each session at $0 — see settleSessionSavings).
 *
 * Transaction dates are day-granularity (YYYY-MM-DD), so sums cover the UTC
 * calendar days the session window touches. Both sides use the same convention,
 * so boundary skew washes out. Pure module — unit-testable.
 */

import type { Transaction } from '@shared/types';
import { isNonSpendTransaction } from '@shared/services/transactions/spendClassification';

export interface SessionSavings {
  /** Net spend during the session's calendar days. */
  actual: number;
  /** Median net spend across the same days 1, 2, and 3 weeks prior. */
  baseline: number;
  /** baseline − actual. Can be negative (spent more than usual). */
  savings: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** ISO dates (YYYY-MM-DD, UTC) covered by [startMs, endMs]. */
function isoDatesCovered(startMs: number, endMs: number): string[] {
  const dates: string[] = [];
  let cursor = Date.UTC(
    new Date(startMs).getUTCFullYear(),
    new Date(startMs).getUTCMonth(),
    new Date(startMs).getUTCDate()
  );
  // Cap at 62 days as a corrupted-data guard; real sessions run 1–3 days.
  for (let i = 0; cursor <= endMs && i < 62; i++) {
    dates.push(new Date(cursor).toISOString().slice(0, 10));
    cursor += DAY_MS;
  }
  return dates;
}

function shiftIsoDate(iso: string, days: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Net spend (purchases minus refunds, non-pending) on the given ISO dates. */
function sumNetSpendOnDates(transactions: Transaction[], dates: Set<string>): number {
  let total = 0;
  for (const tx of transactions) {
    if (tx.amount === 0 || tx.pending) continue;
    // Income, transfers, loan payments, rent and bank fees happen regardless of a
    // no-spend pledge — the shared classification keeps them out of both sums.
    if (isNonSpendTransaction(tx)) continue;
    if (!tx.date || !dates.has(tx.date.slice(0, 10))) continue;
    total += tx.amount;
  }
  return total;
}

function median3(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[1];
}

export function calculateSessionSavings(
  session: { startedAt: number; endsAt: number },
  transactions: Transaction[]
): SessionSavings {
  const sessionDates = isoDatesCovered(session.startedAt, session.endsAt);
  const actual = sumNetSpendOnDates(transactions, new Set(sessionDates));
  const baselines = [7, 14, 21].map(shift =>
    sumNetSpendOnDates(transactions, new Set(sessionDates.map(d => shiftIsoDate(d, -shift))))
  );
  const baseline = median3(baselines);
  const round = (n: number) => Math.round(n * 100) / 100;
  return { actual: round(actual), baseline: round(baseline), savings: round(baseline - actual) };
}
